import { apiClient } from "./client";
import type { PaginatedResponse } from "./types";

export type Stop = {
  id: string;
  namespaced_id: string;
  gtfs_stop_id?: string;
  city_id?: string;
  agency_id?: string | null;
  name: string;
  lat: number;
  lon: number;
  description?: string | null;
  zone_id?: string | null;
  wheelchair_accessible?: boolean | null;
  has_shelter?: boolean | null;
  has_bench?: boolean | null;
  verified?: boolean | null;
};

export type StopPicture = {
  id: string;
  stop_id: string;
  user_id: string;
  stop_name?: string;
  username?: string;
  image_url: string;
  caption: string | null;
  verified: boolean;
  likes_count: number;
  created_at: string;
};

export type StopReview = {
  id: string;
  stop_id: string;
  user_id: string;
  stop_name: string;
  username: string;
  review_type: string;
  suggested_name: string | null;
  suggested_lat: number | null;
  suggested_lon: number | null;
  notes: string | null;
  status: string;
  created_at: string;
};

export const stops = {
  list: (params?: { search?: string; limit?: number; offset?: number }) => {
    const query = new URLSearchParams();
    if (params?.search) query.append("search", params.search);
    query.append("limit", (params?.limit ?? 5000).toString());
    if (params?.offset !== undefined) query.append("offset", params.offset.toString());
    return apiClient.request<PaginatedResponse<Stop>>(`/stops?${query}`, { auth: "public" });
  },
  listByCity: (
    citySlug: string,
    params?: { limit?: number; offset?: number },
  ) => {
    const query = new URLSearchParams();
    query.append("city", citySlug);
    query.append("limit", (params?.limit ?? 5000).toString());
    if (params?.offset !== undefined) query.append("offset", params.offset.toString());
    return apiClient.request<PaginatedResponse<Stop>>(`/stops?${query}`, { auth: "public" });
  },
  create: (data: {
    city_id: string;
    agency_id: string;
    name: string;
    lat: number;
    lon: number;
    description?: string;
    zone_id?: string;
    wheelchair_accessible?: boolean;
    has_shelter?: boolean;
    has_bench?: boolean;
  }) =>
    apiClient.request<Stop>("/stops", { auth: "mod", method: "POST", body: JSON.stringify(data) }),
  update: (
    id: string,
    data: {
      name: string;
      lat: number;
      lon: number;
      description?: string | null;
      zone_id?: string | null;
      wheelchair_accessible?: boolean | null;
      has_shelter?: boolean | null;
      has_bench?: boolean | null;
      agency_id?: string | null;
    },
  ) =>
    apiClient.request<Stop>(`/stops/${id}`, {
      auth: "mod",
      method: "PUT",
      body: JSON.stringify(data),
    }),
  delete: (id: string) =>
    apiClient.request<void>(`/stops/${id}`, { auth: "mod", method: "DELETE" }),
  pictures: {
    list: (stopId: string) =>
      apiClient.request<{ data: StopPicture[]; total: number }>(
        `/stops/${stopId}/pictures`,
        { auth: "public" },
      ),
    upload: (stopId: string, file: any, caption?: string) => {
      const formData = new FormData();
      formData.append("image", file);
      if (caption) formData.append("caption", caption);
      return apiClient.request<StopPicture>(`/stops/${stopId}/pictures`, { auth: "public", method: "POST", body: formData });
    },
    delete: (pictureId: string) =>
      apiClient.request<void>(`/stops/pictures/${pictureId}`, { auth: "public", method: "DELETE" }),
  },
};

export const moderation = {
  stopReviews: {
    list: (params?: { status?: string; limit?: number; offset?: number }) => {
      const query = new URLSearchParams();
      if (params?.status) query.append("status", params.status);
      if (params?.limit !== undefined) query.append("limit", params.limit.toString());
      if (params?.offset !== undefined) query.append("offset", params.offset.toString());
      return apiClient.request<PaginatedResponse<StopReview>>(
        `/moderation/stop-reviews?${query}`,
        { auth: "mod" },
      );
    },
    approve: (id: string) =>
      apiClient.request<void>(`/moderation/stop-reviews/${id}/approve`, {
        auth: "mod",
        method: "POST",
      }),
    delete: (id: string) =>
      apiClient.request<void>(`/moderation/stop-reviews/${id}`, { auth: "mod", method: "DELETE" }),
  },
  stopPictures: {
    list: (params?: { status?: string; limit?: number; offset?: number }) => {
      const query = new URLSearchParams();
      if (params?.status) query.append("status", params.status);
      if (params?.limit !== undefined) query.append("limit", params.limit.toString());
      if (params?.offset !== undefined) query.append("offset", params.offset.toString());
      return apiClient.request<PaginatedResponse<StopPicture>>(
        `/moderation/stop-pictures?${query}`,
        { auth: "mod" },
      );
    },
    approve: (id: string) =>
      apiClient.request<void>(`/moderation/stop-pictures/${id}/approve`, {
        auth: "mod",
        method: "POST",
      }),
  },
};
